import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FakeLink } from '@/core/FakeLink';
import { canWriteItem } from '@/services/authorization.service';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { useVantageWorkbookActions } from '@/vantage/vantageWorkbook.store';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { SeeqNames } from '@seeqdev/constants';
import { Alert } from '@seeqdev/qomponents';
import type { StoppedConditionMonitorState } from '@/vantage/types/VantageWorkbook.types';
import type { ScalarPropertyV1 } from '@seeqdev/api/model/models';

interface VantageConditionMonitorStoppedWarningProps {
  stoppedConditionMonitorState: StoppedConditionMonitorState;
}

/**
 * Warning message displayed when a Vantage condition monitor is disabled and/or archived.
 */
export const VantageConditionMonitorStoppedWarning = ({
  stoppedConditionMonitorState,
}: VantageConditionMonitorStoppedWarningProps) => {
  const { t } = useTranslation();

  const [show, setShow] = useState(true);
  const { setStoppedConditionMonitorState } = useVantageWorkbookActions();

  const hasWritePermission = canWriteItem(stoppedConditionMonitorState);

  if (!show) {
    return null;
  }

  /**
   * If a condition monitor is marked as enabled but does not have a job ID, it indicates that the job was deleted.
   * In this case, we disable the condition monitor temporarily so that it can be re-enabled properly to kick off a new job.
   */
  const disableConditionMonitorIfJobDoesNotExist = async () => {
    if (stoppedConditionMonitorState.isEnabled && !stoppedConditionMonitorState.conditionMonitorJobId) {
      await sqItemsApi.setProperties([{ name: SeeqNames.Properties.Enabled, value: false }], {
        id: stoppedConditionMonitorState.id,
      });
    }
  };

  const enableIndividualConditionMonitor = async () => {
    const isConditionMonitorDisabled =
      !stoppedConditionMonitorState.isEnabled || !stoppedConditionMonitorState.conditionMonitorJobId;

    await disableConditionMonitorIfJobDoesNotExist();

    await sqItemsApi.setProperties(
      new Array<ScalarPropertyV1>()
        .concat(stoppedConditionMonitorState.isArchived ? [{ name: SeeqNames.Properties.Archived, value: false }] : [])
        .concat(isConditionMonitorDisabled ? [{ name: SeeqNames.Properties.Enabled, value: true }] : []),
      { id: stoppedConditionMonitorState.id },
    );
  };

  const handleEnableFromVantage = async () => {
    try {
      await enableIndividualConditionMonitor();
      successToast({ messageKey: 'VANTAGE.CM_WARNING.ENABLE_SUCCESS' });
      setStoppedConditionMonitorState(undefined);
    } catch (e) {
      errorToast({ httpResponseOrError: e });
    }
  };

  return (
    <Alert
      id="vantageConditionMonitorStopped"
      variant="warning"
      dismissible={true}
      extraClassNames="warningBannerHeight"
      onClose={() => setShow(false)}>
      <span data-testid="vantageConditionMonitorStoppedWarning">
        {t('VANTAGE.CM_WARNING.DISABLED')}{' '}
        {hasWritePermission ? (
          <FakeLink onClick={handleEnableFromVantage}>{t('ENABLE')}</FakeLink>
        ) : (
          <>{t('VANTAGE.CM_WARNING.CONTACT_OWNER')}</>
        )}
      </span>
    </Alert>
  );
};
